import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, SlidersHorizontal } from 'lucide-react';
import axiosInstance from '@/utils/axios';
import PropertyCard from '@/components/PropertyCard';
import Pagination from '@/components/Pagination';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

const DEPARTMENTS = ['Cundinamarca', 'Antioquia', 'Valle del Cauca', 'Bolívar', 'Santander', 'Atlántico', 'Magdalena', 'Caldas', 'Risaralda', 'Quindío', 'Meta'];
const CITIES = {
  'Cundinamarca': ['Bogotá', 'Soacha', 'Fusagasugá', 'Chía'],
  'Antioquia': ['Medellín', 'Envigado', 'Rionegro', 'Itagüí'],
  'Valle del Cauca': ['Cali', 'Palmira', 'Buenaventura', 'Tuluá'],
  'Bolívar': ['Cartagena', 'Turbaco'],
  'Santander': ['Bucaramanga', 'Floridablanca', 'Girón'],
  'Atlántico': ['Barranquilla', 'Soledad'],
  'Magdalena': ['Santa Marta'],
  'Caldas': ['Manizales'],
  'Risaralda': ['Pereira'],
  'Quindío': ['Armenia'],
  'Meta': ['Villavicencio'],
};
const PROPERTY_TYPES = [
  { value: 'APARTMENT', label: 'Apartamento' },
  { value: 'HOUSE', label: 'Casa' },
  { value: 'LOT', label: 'Lote' },
  { value: 'FARM', label: 'Finca' },
  { value: 'OFFICE', label: 'Oficina' },
  { value: 'LOCAL', label: 'Local Comercial' },
];

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  const [filters, setFilters] = useState({
    department: searchParams.get('department') || '',
    city: searchParams.get('city') || '',
    operation: searchParams.get('operation') || '',
    property_type: searchParams.get('property_type') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    min_area: searchParams.get('min_area') || '',
    max_area: searchParams.get('max_area') || '',
    rooms: searchParams.get('rooms') || '',
    bathrooms: searchParams.get('bathrooms') || '',
    parking: searchParams.get('parking') || '',
    sort_by: searchParams.get('sort_by') || 'created_at',
  });

  useEffect(() => {
    fetchProperties();
  }, [searchParams]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const params = {
        page: searchParams.get('page') || 1,
        limit: 12,
      };

      Object.keys(filters).forEach((key) => {
        const value = searchParams.get(key) || filters[key];
        if (value) params[key] = value;
      });

      const { data } = await axiosInstance.get('/properties', { params });
      setProperties(data.properties);
      setTotal(data.total);
      setCurrentPage(data.page);
      setTotalPages(data.total_pages);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach((key) => {
      if (filters[key]) params.append(key, filters[key]);
    });
    params.set('page', '1');
    setSearchParams(params);
    setFilterOpen(false);
  };

  const clearFilters = () => {
    setFilters({
      department: '',
      city: '',
      operation: '',
      property_type: '',
      min_price: '',
      max_price: '',
      min_area: '',
      max_area: '',
      rooms: '',
      bathrooms: '',
      parking: '',
      sort_by: 'created_at',
    });
    setSearchParams({});
  };

  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page);
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const FilterContent = () => (
    <div className="space-y-6">
      <div>
        <Label htmlFor="filter-department" className="mb-2 block">Departamento</Label>
        <Select value={filters.department || 'ALL'} onValueChange={(value) => handleFilterChange('department', value === 'ALL' ? '' : value)}>
          <SelectTrigger id="filter-department" data-testid="filter-department">
            <SelectValue placeholder="Todos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos</SelectItem>
            {DEPARTMENTS.map((dept) => (
              <SelectItem key={dept} value={dept}>{dept}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {filters.department && CITIES[filters.department] && (
        <div>
          <Label htmlFor="filter-city" className="mb-2 block">Ciudad</Label>
          <Select value={filters.city || 'ALL'} onValueChange={(value) => handleFilterChange('city', value === 'ALL' ? '' : value)}>
            <SelectTrigger id="filter-city" data-testid="filter-city">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todas</SelectItem>
              {CITIES[filters.department].map((city) => (
                <SelectItem key={city} value={city}>{city}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label htmlFor="filter-operation" className="mb-2 block">Operación</Label>
        <Select value={filters.operation || 'ALL'} onValueChange={(value) => handleFilterChange('operation', value === 'ALL' ? '' : value)}>
          <SelectTrigger id="filter-operation" data-testid="filter-operation">
            <SelectValue placeholder="Todas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todas</SelectItem>
            <SelectItem value="SALE">Venta</SelectItem>
            <SelectItem value="RENT">Arriendo</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="filter-property-type" className="mb-2 block">Tipo de Propiedad</Label>
        <Select value={filters.property_type || 'ALL'} onValueChange={(value) => handleFilterChange('property_type', value === 'ALL' ? '' : value)}>
          <SelectTrigger id="filter-property-type" data-testid="filter-property-type">
            <SelectValue placeholder="Todos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos</SelectItem>
            {PROPERTY_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="mb-2 block">Precio (COP)</Label>
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="number"
            placeholder="Mínimo"
            value={filters.min_price}
            onChange={(e) => handleFilterChange('min_price', e.target.value)}
            data-testid="filter-min-price"
          />
          <Input
            type="number"
            placeholder="Máximo"
            value={filters.max_price}
            onChange={(e) => handleFilterChange('max_price', e.target.value)}
            data-testid="filter-max-price"
          />
        </div>
      </div>

      <div>
        <Label className="mb-2 block">Área (m²)</Label>
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="number"
            placeholder="Mínimo"
            value={filters.min_area}
            onChange={(e) => handleFilterChange('min_area', e.target.value)}
            data-testid="filter-min-area"
          />
          <Input
            type="number"
            placeholder="Máximo"
            value={filters.max_area}
            onChange={(e) => handleFilterChange('max_area', e.target.value)}
            data-testid="filter-max-area"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label htmlFor="filter-rooms" className="mb-2 block text-sm">Hab.</Label>
          <Input
            id="filter-rooms"
            type="number"
            min="0"
            placeholder="0"
            value={filters.rooms}
            onChange={(e) => handleFilterChange('rooms', e.target.value)}
            data-testid="filter-rooms"
          />
        </div>
        <div>
          <Label htmlFor="filter-bathrooms" className="mb-2 block text-sm">Baños</Label>
          <Input
            id="filter-bathrooms"
            type="number"
            min="0"
            placeholder="0"
            value={filters.bathrooms}
            onChange={(e) => handleFilterChange('bathrooms', e.target.value)}
            data-testid="filter-bathrooms"
          />
        </div>
        <div>
          <Label htmlFor="filter-parking" className="mb-2 block text-sm">Parq.</Label>
          <Input
            id="filter-parking"
            type="number"
            min="0"
            placeholder="0"
            value={filters.parking}
            onChange={(e) => handleFilterChange('parking', e.target.value)}
            data-testid="filter-parking"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="filter-sort" className="mb-2 block">Ordenar por</Label>
        <Select value={filters.sort_by} onValueChange={(value) => handleFilterChange('sort_by', value)}>
          <SelectTrigger id="filter-sort" data-testid="filter-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="created_at">Más recientes</SelectItem>
            <SelectItem value="price_asc">Menor precio</SelectItem>
            <SelectItem value="price_desc">Mayor precio</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col space-y-2">
        <Button onClick={applyFilters} className="bg-primary hover:bg-primary-hover" data-testid="apply-filters-button">
          Aplicar Filtros
        </Button>
        <Button onClick={clearFilters} variant="outline" data-testid="clear-filters-button">
          Limpiar Filtros
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-2">
              Buscar Propiedades
            </h1>
            <p className="text-slate-600">
              {loading ? 'Cargando...' : `${total} propiedad${total !== 1 ? 'es' : ''} encontrada${total !== 1 ? 's' : ''}`}
            </p>
          </div>

          {/* Mobile Filter Button */}
          <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="lg:hidden" data-testid="mobile-filter-button">
                <Filter className="w-4 h-4 mr-2" />
                Filtros
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Filtros de Búsqueda</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <FilterContent />
              </div>
            </SheetContent>
          </Sheet>
        </div>

        <div className="flex gap-8">
          {/* Desktop Sidebar Filters */}
          <aside className="hidden lg:block w-80 flex-shrink-0">
            <div className="bg-white rounded-2xl p-6 border border-slate-200 sticky top-24" data-testid="desktop-filters">
              <div className="flex items-center mb-6">
                <SlidersHorizontal className="w-5 h-5 mr-2 text-primary" />
                <h2 className="text-xl font-heading font-bold text-slate-900">Filtros</h2>
              </div>
              <FilterContent />
            </div>
          </aside>

          {/* Properties Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="h-96 bg-slate-200 rounded-2xl animate-pulse" />
                ))}
              </div>
            ) : properties.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8" data-testid="properties-grid">
                  {properties.map((property) => (
                    <PropertyCard key={property.id} property={property} />
                  ))}
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </>
            ) : (
              <div className="text-center py-16" data-testid="no-results">
                <p className="text-xl text-slate-600 mb-4">No se encontraron propiedades con los filtros seleccionados</p>
                <Button onClick={clearFilters} variant="outline">
                  Limpiar Filtros
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;